import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import { ownResourceKey } from '../../../ownKey'
import { initialOwnWords, ownWordsReducer, type OwnNotice, type OwnSending, type OwnToolKey, type OwnWordsAction, type OwnWordsState } from './words'

/**
 * Ход операции окна «Собственная» по источникам: какая форма открыта под «Подробнее», что набрано, что сейчас уходит агенту, какой
 * отказ агента висит рядом с кнопкой и какие заметки остались после продажи (намерение кассира). Срок жизни `screen`: живёт, пока
 * открыто окно (хук продажи сбрасывает при закрытии), в хранилище браузера не попадает; правду об операции после F5 говорит журнал
 * агента. Правила переходов — чистая функция `ownWordsReducer` (`words.ts`), слайс только раскладывает по источникам.
 */
export interface OwnOperationSliceState {
  byResource: Record<string, OwnWordsState>
}

const initialState: OwnOperationSliceState = { byResource: {} }

type Payload<T = unknown> = PayloadAction<{ resourceId: string } & T>

function apply(state: OwnOperationSliceState, resourceId: string, action: OwnWordsAction): void {
  const key = ownResourceKey(resourceId)
  const stored = state.byResource[key]
  const before = stored ?? initialOwnWords
  const after = ownWordsReducer(before, action)
  if (after !== before) state.byResource[key] = after
}

const slice = createSlice({
  name: 'own/operation',
  initialState,
  reducers: {
    ownParkOpened(state, { payload }: Payload) {
      apply(state, payload.resourceId, { type: 'park-opened' })
    },
    ownParkClosed(state, { payload }: Payload) {
      apply(state, payload.resourceId, { type: 'park-closed' })
    },
    ownToolOpened(state, { payload }: Payload<{ key: OwnToolKey }>) {
      apply(state, payload.resourceId, { type: 'tool-opened', key: payload.key })
    },
    ownToolField(state, { payload }: Payload<{ field: string; value: string }>) {
      apply(state, payload.resourceId, { type: 'tool-field', field: payload.field, value: payload.value })
    },
    ownToolClosed(state, { payload }: Payload) {
      apply(state, payload.resourceId, { type: 'tool-closed' })
    },
    ownSending(state, { payload }: Payload<{ what: OwnSending | null }>) {
      apply(state, payload.resourceId, { type: 'sending', what: payload.what })
    },
    ownWordRefused(state, { payload }: Payload<{ operationId: string; code: string | null; text: string }>) {
      apply(state, payload.resourceId, { type: 'refused', operationId: payload.operationId, code: payload.code, text: payload.text })
    },
    ownWordAccepted(state, { payload }: Payload) {
      apply(state, payload.resourceId, { type: 'accepted' })
    },
    ownNoticeAdded(state, { payload }: Payload<{ notice: OwnNotice }>) {
      apply(state, payload.resourceId, { type: 'notice-added', notice: payload.notice })
    },
    ownNoticeRemoved(state, { payload }: Payload<{ key: string }>) {
      apply(state, payload.resourceId, { type: 'notice-removed', key: payload.key })
    },
    ownOperationChanged(state, { payload }: Payload) {
      apply(state, payload.resourceId, { type: 'operation-changed' })
    },
    /** Окно закрыто: формы, отказ и заметки этого окна не переходят в следующее. */
    ownOperationWindowClosed(state, { payload }: Payload) {
      delete state.byResource[ownResourceKey(payload.resourceId)]
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      delete state.byResource[ownResourceKey(action.payload.resourceId)]
    })
  },
})

export const {
  ownParkOpened,
  ownParkClosed,
  ownToolOpened,
  ownToolField,
  ownToolClosed,
  ownSending,
  ownWordRefused,
  ownWordAccepted,
  ownNoticeAdded,
  ownNoticeRemoved,
  ownOperationChanged,
  ownOperationWindowClosed,
} = slice.actions
export const ownOperationSliceReducer = slice.reducer

/** Ход операции источника; нет записи — формы закрыты, заметок нет. */
export const operationOf = (state: OwnOperationSliceState, resourceId: string): OwnWordsState => state.byResource[ownResourceKey(resourceId)] ?? initialOwnWords
